import { Icon } from "@iconify-icon/react";
import { Modal } from "antd";
import Button from "Components/Button";
import { OrderDetails, useOrder } from "hooks/OrderManagement/useOrders";
import { useUpdateOrderMutationCopy } from "hooks/OrderManagement/useUpdateOrderMutationCopy";
import { useBoolean } from "hooks/useBoolean";
import { RefreshCcw } from "lucide-react";
import { useEffect, useState } from "react";
import { formatCurrency } from "utils/formatNumber";

type AcceptOrderProps = {
  data: OrderDetails;
};
export const AcceptOrder = ({ data }: AcceptOrderProps) => {
  const [acceptModal, setAcceptModal] = useBoolean(false);
  const [declineModal, setDeclineModal] = useBoolean(false);
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const { refetch, acceptOrderMutation, acceptOrderLoading } = useOrder(
    data?.id
  );
  const [payload, setPayload] = useState({
    products: [],
  });

  const [updatedData, setUpdatedData] = useState(
    data?.orderProducts.map((product: any) => ({
      ...product,
      recommendedUnit: product.recommendedUnit || product.unit,
    }))
  );

  const onIncrement = (id: string) => {
    setUpdatedData((prevState: any) =>
      prevState.map((product: any) =>
        product.id === id
          ? {
              ...product,
              recommendedUnit: product.recommendedUnit + 1,
              totalAmount: (product.recommendedUnit + 1) * product.price,
            }
          : product
      )
    );

    setPayload((prevPayload: any) => {
      const updatedProduct = updatedData.find(
        (product: any) => product.id === id
      );
      const updatedQuantity = updatedProduct?.recommendedUnit + 1;

      const existingProduct = prevPayload.products.find(
        (p: any) => p.id === id
      );

      if (existingProduct) {
        return {
          ...prevPayload,
          products: prevPayload.products.map((p: any) =>
            p.id === id ? { ...p, quantity: updatedQuantity } : p
          ),
        };
      } else {
        return {
          ...prevPayload,
          products: [
            ...prevPayload.products,
            { id, quantity: updatedQuantity },
          ],
        };
      }
    });
  };

  const onDecrement = (id: string) => {
    setUpdatedData((prevState: any) =>
      prevState.map((product: any) =>
        product.id === id
          ? {
              ...product,
              recommendedUnit: Math.max(1, product.recommendedUnit - 1),
              totalAmount:
                Math.max(1, product.recommendedUnit - 1) * product.price,
            }
          : product
      )
    );

    setPayload((prevPayload: any) => {
      const updatedProduct = updatedData.find(
        (product: any) => product.id === id
      );
      const updatedQuantity = Math.max(1, updatedProduct.recommendedUnit - 1);

      const existingProduct = prevPayload.products.find(
        (p: any) => p.id === id
      );

      if (existingProduct && updatedQuantity > 0) {
        return {
          ...prevPayload,
          products: prevPayload.products.map((p: any) =>
            p.id === id ? { ...p, quantity: updatedQuantity } : p
          ),
        };
      } else if (!existingProduct && updatedQuantity > 0) {
        return {
          ...prevPayload,
          products: [
            ...prevPayload.products,
            { id, quantity: updatedQuantity },
          ],
        };
      }

      return prevPayload;
    });
  };

  const handleSuccess = () => {
    setLoading(false);
    refetch();
    setAcceptModal.off();
  };

  const { mutateAsync } = useUpdateOrderMutationCopy({
    id: data?.id,
    payload: hasChanges ? payload : {},
    handleSuccess,
  });

  const handleSubmit = () => {
    mutateAsync();
    setLoading(true);
  };

  useEffect(() => {
    const changesMade = updatedData?.some(
      (updatedProduct: any, index: number) => {
        const originalProduct = data?.orderProducts[index];
        return (
          updatedProduct.recommendedUnit !== originalProduct.unit ||
          updatedProduct.totalAmount !== originalProduct.totalAmount
        );
      }
    );

    setHasChanges(changesMade);
  }, [data, updatedData]);

  const resetData = () => {
    setUpdatedData(
      data?.orderProducts.map((product: any) => ({
        ...product,
        recommendedUnit: product.recommendedUnit || product.unit,
      }))
    );
    setPayload({ products: [] });
    setHasChanges(false);
  };

  return (
    <div>
      <Button label="Accept Order" onClick={setAcceptModal.on} />
      <Modal
        open={acceptModal}
        onCancel={setAcceptModal.off}
        footer={null}
        title="Accept Order"
      >
        <div className="grid gap-y-4 py-2">
          <p className="text-primary-foreground text-lg">
            Are you sure you want to accept this order, this action can not be
            reversed
          </p>

          {hasChanges && (
            <div className="flex justify-between items-center">
              <span className="bg-primary-light rounded-full p-[5px] text-orange">
                Order Resized
              </span>
              <button
                onClick={resetData}
                className="flex items-center gap-2 bg-[#F3F3F3] text-[14px] p-2 rounded-full"
              >
                <RefreshCcw size={15} /> Reset order
              </button>
            </div>
          )}

          <div className="mb-4">
            {updatedData &&
              updatedData.map(
                ({
                  id,
                  productName,
                  image,
                  totalAmount,
                  recommendedUnit,
                }: any) => (
                  <div className="flex flex-row gap-2 mb-2" key={id}>
                    <img
                      src={image || "https://via.placeholder.com/50"}
                      alt="product"
                      className="w-[70px] h-[70px] border-[0.86px] border-[#eeeeee] rounded-md"
                    />
                    <div className="flex-1">
                      <div>
                        <h3 className="mt-4 font-normal text">{productName}</h3>
                        <div className="flex justify-between items-center">
                          <div className="flex items-center space-x-3 mt-1">
                            <button
                              title="-"
                              onClick={() => onDecrement(id)}
                              className="size-5 flex justify-center items-center rounded-full bg-[#F3F3F3] hover:bg-[#F3F3F3] focus:outline-none"
                            >
                              <Icon
                                icon={"hugeicons:remove-01"}
                                className="text-[#414141] font-bold stroke-2"
                                width={13}
                              />
                            </button>
                            <span className="text-sm font-bold min-w-4">
                              {recommendedUnit}
                            </span>
                            <button
                              title="+"
                              onClick={() => onIncrement(id)}
                              className="size-5 flex justify-center items-center rounded-full bg-[#F3F3F3] hover:bg-[#F3F3F9] focus:outline-none"
                            >
                              <Icon
                                icon={"hugeicons:add-01"}
                                className="text-[#414141]"
                                width={13}
                              />
                            </button>
                          </div>
                          <span>{formatCurrency(totalAmount / 100)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              )}
          </div>
          <div className="flex space-x-2">
            <Button label="Yes" onClick={handleSubmit} isLoading={loading} />
            <Button
              label="No"
              onClick={setDeclineModal.off}
              className="bg-[#E8E8E8] text-[#2E2E2E]"
            />
          </div>

          {/* <div className="flex space-x-2">
            <Button
              label="Yes"
              isLoading={acceptOrderLoading}
              onClick={() => {
                acceptOrderMutation(undefined, {
                  onSuccess: () => {
                    refetch();
                    setAcceptModal.off();
                  },
                });
              }}
            />
            <Button
              label="No"
              onClick={setAcceptModal.off}
              className="bg-[#E8E8E8] text-[#2E2E2E]"
            />
          </div> */}
        </div>
      </Modal>
    </div>
  );
};
